import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
	useGetListViewResponseMutation,
	useGetRemarksMutation,
	useUpdateProductPriorityMutation
} from '@/features/my-dashborad/api/myDashboardApi';
import { clearDesignViewModalInfo, setDesignViewModalInfo } from '@/store/actions/DesignViewModalAction';
import { isPageReachBottom } from '@/utils/Util';
import LoaderComponent from '@/components/LoaderComponent';
import { toastError, toastSuccess } from '@/components/Toast';
import { generateProductListParams } from '@/features/filter/static/filterUtil';

import ConstingListView from './list-table/ConstingListView';
import SampleJourneyList from './list-table/SampleJourneyList';
import GeneralListView from './list-table/GeneralListView';
import GrapichsListView from './list-table/GrapichsListView';
import FactoryDevelopmentList from './list-table/FactoryDevelopmentList';
import IssueListView from './list-table/IssueListView';
import ThreeDListView from './list-table/ThreeDListView';

const PAGE_SIZE = 20;

const MyDashboardListView = ({
	filterId,
	listViewType,
	selectedProducts = [],
	setSelectedProducts = () => {},
	onListDataUpdate = () => {},
	searchKey = '',
	selectedPriority = null,
	selectedGroupBy = null
}) => {
	const listType = String(listViewType ?? '').toLowerCase();
	const dispatch = useDispatch();
	const [selectedProductId, setSelectedProductId] = useState(null);
	const [isShowCosting, setIsShowCosting] = useState(false);
	const [selectedCostingIds, setSelectedCostingIds] = useState([]);
	const [mergedListViewResponse, setMergedListViewResponse] = useState(null);
	const [pageNumber, setPageNumber] = useState(0);
	const listViewRef = useRef();
	const isFetchingRef = useRef(false);
	const isPageChangingRef = useRef(false);
	const hasMoreRef = useRef(true);
	const filterfields = useSelector((store) => store.filterFieldStore);

	const [getListViewResponse, { data, isLoading: isFetching }] = useGetListViewResponseMutation();

	const [updateProductPriority, { isLoading: isUpdatingPriority }] = useUpdateProductPriorityMutation();
	const [getRemarks, { isLoading: isUpdatingRemarks }] = useGetRemarksMutation();

	//filter body
	const listViewRequestBody = useMemo(() => {
		const filterBody = generateProductListParams(filterfields);
		return {
			...filterBody,
			...(searchKey && { referenceNumber: searchKey }),
			...(selectedPriority?.id && { searchSortingOption: selectedPriority.id })
		};
	}, [filterfields, searchKey, selectedPriority]);

	const listViewRequest = useMemo(
		() =>
			filterId
				? {
						id: filterId,
						params: { page: pageNumber, size: PAGE_SIZE },
						body: listViewRequestBody
					}
				: null,
		[filterId, pageNumber, listViewRequestBody]
	);
	//refetch list view response
	const refetchListViewResponse = useCallback(() => {
		if (!listViewRequest) return Promise.resolve();
		return getListViewResponse(listViewRequest).unwrap();
	}, [listViewRequest, getListViewResponse]);

	useEffect(() => {
		if (listViewRequest) getListViewResponse(listViewRequest);
	}, [listViewRequest, getListViewResponse]);

	//update priority
	const handleUpdatePriority = async (priority) => {
		try {
			await updateProductPriority({
				productId: priority?.id,
				body: {
					priority: priority?.taskPriority
				}
			}).unwrap();
			refetchListViewResponse();
			toastSuccess('Priority Updated Successfully!');
		} catch ({ error }) {
			toastError(error?.data?.message || 'Something went wrong');
		}
	};

	//remarks
	const handleGetRemarks = async (item, remarks) => {
		const details = item;
		const prevRemarks = (details?.remarks ?? '')?.trim();
		if (prevRemarks === remarks?.trim()) return;
		try {
			await getRemarks({
				id:
					details?.sampleItemId ||
					details?.inHouseDevelopmentJourneyDetails?.sampleItemId ||
					details?.factoryDevelopmentJourneyDetails?.sampleItemId,
				remarks: remarks
			}).unwrap();
			toastSuccess('Remarks updated successfully');
		} catch ({ error }) {
			toastError(error?.data?.message || 'Something went wrong');
		}
	};

	///pagination loader
	isFetchingRef.current = isFetching;
	useEffect(() => {
		if (!isFetching) {
			isPageChangingRef.current = false;
		}
	}, [isFetching]);

	useEffect(() => {
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	useEffect(() => {
		window.scrollTo(0, 0);
		if (!filterId) {
			setMergedListViewResponse(null);
			listViewRef.current = null;
			setPageNumber(0);
			hasMoreRef.current = true;
			setSelectedProducts([]);
			setSelectedCostingIds([]);
			onListDataUpdate(null);
			return;
		}
		setPageNumber(0);
		hasMoreRef.current = true;
		setSelectedProducts([]);
		setSelectedCostingIds([]);
		onListDataUpdate(null);
	}, [filterId, listViewType, listViewRequestBody]);

	useEffect(() => {
		if (!filterId || data == null) return;

		const chunk = data.data ?? data.content ?? [];
		hasMoreRef.current = chunk.length === PAGE_SIZE;

		if (pageNumber === 0) {
			const next = { ...data, data: chunk };
			listViewRef.current = next;
			setMergedListViewResponse(next);
			onListDataUpdate(next);
		} else {
			setMergedListViewResponse((prev) => {
				const mergedRows = [...(prev?.data ?? []), ...chunk];
				const next = { ...data, data: mergedRows };
				listViewRef.current = next;
				onListDataUpdate(next);
				return next;
			});
		}
	}, [filterId, data, pageNumber]);

	const handleScroll = async () => {
		if (isFetchingRef.current || isPageChangingRef.current || !hasMoreRef.current || !isPageReachBottom())
			return;

		isPageChangingRef.current = true;
		setPageNumber((prevPage) => prevPage + 1);
	};

	//open techpack view modal
	useEffect(() => {
		if (selectedProductId !== null) {
			openDesignViewModal(selectedProductId);
		}
	}, [selectedProductId]);

	const openDesignViewModal = (selectedProductId) => {
		dispatch(
			setDesignViewModalInfo({
				id: selectedProductId,
				onCloseViewModal: () => {
					setSelectedProductId(null);
					setIsShowCosting(false);
					dispatch(clearDesignViewModalInfo({}));
				},
				source: 'ORDER_DETAILS',
				isShowCostingPanel: isShowCosting,
				isShowCostingButton: true
			})
		);
	};

	const handleDesignImageClick = (productId) => {
		setIsShowCosting(false);
		setSelectedProductId(productId);
	};

	/// checkbox wise sample journey selection
	const handleSelectSampleItem = (e) => {
		const sampleId = e.target.value;
		if (e.target.checked) {
			setSelectedProducts((prev) => [...new Set([...prev, parseInt(sampleId)])]);
		} else {
			setSelectedProducts((prev) => prev.filter((id) => id !== parseInt(sampleId)));
		}
	};

	const handleSelectAllSampleItem = (e) => {
		const sampleList = mergedListViewResponse?.data ?? [];
		const allSampleIds = sampleList.map((item) => parseInt(item?.id)).filter((id) => !Number.isNaN(id));
		if (e.target.checked) {
			setSelectedProducts((prev) => [...new Set([...prev, ...allSampleIds])]);
		} else {
			setSelectedProducts((prev) => prev.filter((id) => !allSampleIds.includes(id)));
		}
	};

	const renderListView = (listViewResponse) => {
		switch (true) {
			case listType.includes('sample_in_house_development'):
				return (
					<SampleJourneyList
						listViewResponse={listViewResponse}
						onClickDesignImage={handleDesignImageClick}
						handleSelectSampleItem={handleSelectSampleItem}
						handleSelectAllSampleItem={handleSelectAllSampleItem}
						selectedSampleItem={selectedProducts}
						handleUpdatePriority={handleUpdatePriority}
						handleGetRemarks={handleGetRemarks}
						isUpdatingPriority={isUpdatingPriority}
						isUpdatingRemarks={isUpdatingRemarks}
						selectedGroupBy={selectedGroupBy}
					/>
				);
			case listType.includes('sample_factory_development'):
				return (
					<FactoryDevelopmentList
						listViewResponse={listViewResponse}
						onClickDesignImage={handleDesignImageClick}
						handleSelectSampleItem={handleSelectSampleItem}
						handleSelectAllSampleItem={handleSelectAllSampleItem}
						selectedSampleItem={selectedProducts}
						handleUpdatePriority={handleUpdatePriority}
						handleGetRemarks={handleGetRemarks}
						isUpdatingPriority={isUpdatingPriority}
						isUpdatingRemarks={isUpdatingRemarks}
						selectedGroupBy={selectedGroupBy}
					/>
				);
			case listType.includes('costing'):
				return (
					<ConstingListView
						listViewResponse={listViewResponse}
						onClickDesignImage={handleDesignImageClick}
						selectedIds={selectedCostingIds}
						setSelectedIds={setSelectedCostingIds}
						onRefreshList={refetchListViewResponse}
						selectedGroupBy={selectedGroupBy}
					/>
				);
			case listType.includes('issue'):
				return (
					<IssueListView
						listViewResponse={listViewResponse}
						handleSelectSampleItem={handleSelectSampleItem}
						handleSelectAllSampleItem={handleSelectAllSampleItem}
						onClickDesignImage={handleDesignImageClick}
						selectedSampleItem={selectedProducts}
						selectedGroupBy={selectedGroupBy}
					/>
				);
			case listType.includes('graphics'):
				return (
					<GrapichsListView
						listViewResponse={listViewResponse}
						onClickDesignImage={handleDesignImageClick}
						handleSelectSampleItem={handleSelectSampleItem}
						handleSelectAllSampleItem={handleSelectAllSampleItem}
						selectedSampleItem={selectedProducts}
						refetchList={refetchListViewResponse}
						selectedGroupBy={selectedGroupBy}
					/>
				);
			case listType.includes('three_d'):
				return (
					<ThreeDListView
						listViewResponse={listViewResponse}
						onClickDesignImage={handleDesignImageClick}
						handleSelectSampleItem={handleSelectSampleItem}
						handleSelectAllSampleItem={handleSelectAllSampleItem}
						selectedSampleItem={selectedProducts}
						selectedGroupBy={selectedGroupBy}
					/>
				);
			default:
				return (
					<GeneralListView
						listViewResponse={listViewResponse}
						onClickDesignImage={handleDesignImageClick}
						handleSelectSampleItem={handleSelectSampleItem}
						handleSelectAllSampleItem={handleSelectAllSampleItem}
						selectedSampleItem={selectedProducts}
						selectedGroupBy={selectedGroupBy}
					/>
				);
		}
	};

	return (
		<div>
			<LoaderComponent loading={isFetching || isUpdatingPriority || isUpdatingRemarks}>
				{renderListView(mergedListViewResponse?.data ?? [])}
			</LoaderComponent>
		</div>
	);
};
export default MyDashboardListView;
